/**
 * Предикаты видимости разделов и вкладок.
 *
 * Вынесены из `App.tsx` намеренно (16.09): тесты на них импортировали `App`,
 * а вместе с ним — весь граф приложения. На CI это удвоило время фронтового
 * прогона (152 → 351 с) и посыпало пограничные асинхронные тесты по таймауту
 * в 5 с, причём каждый прогон падал свой набор. Здесь только чистые функции
 * от `Me`, без импорта компонентов.
 *
 * Источник правды — всегда бэкенд: флаги в `/api/me/`. Если фронт начнёт
 * решать сам, он разойдётся с гейтом на сервере, и человек получит либо
 * невидимую вкладку, либо 403 на видимой.
 */
import type { Me } from "./api/client";

export function canApproveAbsences(me: Me): boolean {
  if (me.is_line_manager || me.is_hr) return true;
  // US-107: у руководителя проекта появилась своя ступень согласования
  // отсутствий — без этого он не увидел бы вкладку вовсе.
  if (me.is_project_manager) return true;
  if (
    me.roles &&
    me.roles.some((r) => r === "line_manager" || r === "hr" || r === "project_manager")
  ) {
    return true;
  }
  // Fallback for early backend states: staff/superuser can also approve.
  return me.is_staff || me.is_superuser;
}

/** US-107: ступень ЛР/HR отдельно от ступени РП — чистый руководитель проекта
 *  не должен видеть очередь линейного руководителя (она у него пустая и
 *  выглядит как ошибка доступа). */
export function canApproveAbsencesAsLRorHR(me: Me): boolean {
  if (me.is_line_manager || me.is_hr) return true;
  if (me.roles && me.roles.some((r) => r === "line_manager" || r === "hr")) return true;
  return me.is_staff || me.is_superuser;
}

export function canApproveTimesheetsAsLR(me: Me): boolean {
  if (me.is_line_manager) return true;
  if (me.roles && me.roles.some((r) => r === "line_manager")) return true;
  return me.is_staff || me.is_superuser;
}

export function canApproveTimesheetsAsPM(me: Me): boolean {
  return !!me.is_project_manager;
}

/** Видимость пункта «На согласовании» — LR / PM / HR / staff. */
export function canSeeApprovals(me: Me): boolean {
  return canApproveAbsences(me) || canApproveTimesheetsAsLR(me) || canApproveTimesheetsAsPM(me);
}

/** Видимость пункта «Сводка» (дашборд US-93) — у кого РЕАЛЬНО есть команда. */
export function canSeeDashboard(me: Me): boolean {
  // Источник правды — backend `user_is_manager` (поле `is_dashboard_manager`):
  // фактически есть подчинённые / проекты / PMO, а НЕ просто роль `line_manager`.
  // Иначе носитель роли без команды (напр. бывший делегат, у кого роль осталась —
  // снятие делегата её не отзывает) видел «Сводку» и ловил 403. Так фронт и бэк
  // сходятся: нет команды → нет пункта «Сводка», лендинг на таймшит.
  return !!me.is_dashboard_manager;
}

/** Своды (недельный / месячный / исключения) — управленческие отчёты. */
export function canSeeSummaryReports(me: Me): boolean {
  if (me.is_pmo || me.is_line_manager || me.is_project_manager) return true;
  if (me.is_staff || me.is_superuser) return true;
  if (
    me.roles &&
    me.roles.some((r) =>
      ["project_office_head", "line_manager", "project_manager", "admin"].includes(r),
    )
  ) {
    return true;
  }
  return false;
}

/** Право на выгрузку 1С ЗУП (US-47.1): роль `zup_export` + admin/staff. */
export function canExportZup(me: Me): boolean {
  return me.can_export_zup ?? (me.is_staff || me.is_superuser);
}

/**
 * US-111: вкладка «Резервный отчёт». Выделенная роль `reserve_report` +
 * admin/staff. Источник правды — backend `can_see_reserve_report`.
 */
export function canSeeReserveReport(me: Me): boolean {
  return me.can_see_reserve_report ?? (me.is_staff || me.is_superuser);
}

/**
 * Раздел «Отчёты» в меню. US-47.1: открывается и тем, у кого есть ТОЛЬКО право
 * на выгрузку 1С (бухгалтер/кадровик вне управленческой вертикали) — иначе
 * носитель роли не увидел бы ни раздела, ни своей вкладки.
 */
export function canSeeReports(me: Me): boolean {
  return canSeeSummaryReports(me) || canExportZup(me) || canSeeReserveReport(me);
}

/**
 * US-113: раздел «Ставки» — выделенная роль `rate_manage` + admin/staff.
 * Не `hr`: эта роль есть и у внешних кадровиков, им ставки всей компании не
 * положены. Источник правды — backend `can_manage_rates`.
 */
export function canManageRates(me: Me): boolean {
  return me.can_manage_rates ?? (me.is_staff || me.is_superuser);
}
